import { createFileRoute } from "@tanstack/react-router";
import getPageTitle from "lib/utils/getPageTitle";
import type { JSX, ChangeEvent } from "react";
import { useState, useCallback } from "react";
import { Helmet as Head } from "react-helmet-async";
import Button from "ui/buttons/Button";
import OptimizedImage from "ui/components/OptimizedImage";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import styles from "./image-service.module.scss";

interface ImageParams {
  url: string;
  width?: number;
  height?: number;
  quality: number;
  format?: string;
  fit: string;
}

const formatOptions = [
  { label: "Original", value: "" },
  { label: "JPEG", value: "jpeg" },
  { label: "PNG", value: "png" },
  { label: "WebP", value: "webp" },
  { label: "AVIF", value: "avif" },
];

const fitOptions = [
  { label: "Cover", value: "cover" },
  { label: "Contain", value: "contain" },
  { label: "Fill", value: "fill" },
  { label: "Inside", value: "inside" },
  { label: "Outside", value: "outside" },
];

// Sample S3 URLs from different allowed buckets
const sampleImages = [
  "https://aekveym7hu-product-prod.s3.amazonaws.com/home_editor/param_sales_images/AQok68c4fDo8oEjaCovered_Patio_%25232.png",
  "https://aekveym7hu-product-prod.s3.us-east-1.amazonaws.com/master_plans/k8E7M6Gp4rGyqRxp/sales_images/elevationStyle/jTKiaIiodreqOfwP/lVsRTJ9xynMbMWmQIBS_Transitional_WEB.jpg",
  "https://aekveym7hu-product-prod.s3.us-east-1.amazonaws.com/master_plans/Oq9AdGwE84Y3Jy7Q/sales_images/elevationStyle/ZWkNrFoDPr1g2tyH/aLosRwD4k3NANOL4Avery_A_MLS.jpg",
  "https://aekveym7hu-product-prod.s3.us-east-1.amazonaws.com/master_plans/k8E7M6Gp4rGyqRxp/generated_elevations/rSvWsoDw7IJ8G5oRwjgwPIRb38oILWBv.png",
  "https://aekveym7hu-product-prod.s3.us-east-1.amazonaws.com/home_editor/param_sales_images/wrmjOM7GWa8ExfzIPorch_-_Screened.jpg",
  "https://aekveym7hu-product-prod.s3.us-east-1.amazonaws.com/home_editor/param_sales_images/qiKyHQn44jVprlFIPorch_-_Office.jpg",
];

function ImageServicePage(): JSX.Element {
  const [params, setParams] = useState<ImageParams>({
    url: sampleImages[0],
    quality: 80,
    fit: "cover",
  });

  // Determine if we're in development mode to use local proxy
  const isDevelopment = process.env.NODE_ENV === "development" || process.env.PUBLIC_IS_DEV_MODE === "true";

  const [displayParams, setDisplayParams] = useState<ImageParams | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const handleParamChange = useCallback((key: keyof ImageParams, value: string | number | undefined): void => {
    setParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleLoadImages = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError("");

    try {
      // Set the display parameters to trigger image loading
      setDisplayParams({ ...params });
    } catch (err) {
      setError(`Failed to load images: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  const handleImageError = useCallback(
    (type: string) =>
      (e: ChangeEvent<HTMLImageElement>): void => {
        console.error(`${type} image failed to load:`, e.target.src);
        setError(`Failed to load ${type.toLowerCase()} image`);
      },
    [],
  );

  const handleUrlChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      handleParamChange("url", e.target.value);
    },
    [handleParamChange],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1>Image Optimization Service Test</h1>
        <p className={styles.env}>
          Environment: {isDevelopment ? "Development (Local Proxy)" : "Production (CloudFront)"}
        </p>
        <p className={styles.description}>
          Enter a full S3 URL from an allowed bucket. The service will validate the bucket and optimize the image.
        </p>
      </div>

      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <label>Sample Images:</label>
          <Select
            value={params.url}
            onChange={(value) => handleParamChange("url", value as string)}
            options={sampleImages.map((url) => ({
              label: url.split("/").pop() || url,
              value: url,
            }))}
          />
        </div>

        <div className={styles.controlGroup}>
          <label>Custom S3 URL:</label>
          <Input
            type="url"
            value={params.url}
            onChange={handleUrlChange}
            placeholder="https://bucket-name.s3.region.amazonaws.com/path/to/image.jpg"
            className={styles.urlInput}
          />
        </div>

        <div className={styles.controlRow}>
          <div className={styles.controlGroup}>
            <label>Width (px):</label>
            <Input
              type="number"
              value={params.width?.toString() || ""}
              onChange={(e) => handleParamChange("width", e.currentTarget.value ? parseInt(e.target.value) : undefined)}
              placeholder="Auto"
              min={1}
              max={4000}
            />
          </div>

          <div className={styles.controlGroup}>
            <label>Height (px):</label>
            <Input
              type="number"
              value={params.height?.toString() || ""}
              onChange={(e) =>
                handleParamChange("height", e.currentTarget.value ? parseInt(e.target.value) : undefined)
              }
              placeholder="Auto"
              min={1}
              max={4000}
            />
          </div>
        </div>

        <div className={styles.controlRow}>
          <div className={styles.controlGroup}>
            <label>Quality (1-100):</label>
            <Input
              type="number"
              value={params.quality.toString()}
              onChange={(e) => handleParamChange("quality", parseInt(e.target.value))}
              min={1}
              max={100}
            />
          </div>

          <div className={styles.controlGroup}>
            <label>Format:</label>
            <Select
              value={params.format || ""}
              onChange={(value) => handleParamChange("format", (value as string) || "")}
              options={formatOptions}
            />
          </div>

          <div className={styles.controlGroup}>
            <label>Fit:</label>
            <Select
              value={params.fit}
              onChange={(value) => handleParamChange("fit", value as string)}
              options={fitOptions}
            />
          </div>
        </div>

        <Button onClick={handleLoadImages} disabled={isLoading}>
          {isLoading ? "Loading..." : "Load Images"}
        </Button>
      </div>

      {error && <div className={styles.error}>Error: {error}</div>}

      {displayParams && (
        <div className={styles.results}>
          <div className={styles.imageComparison}>
            <div className={styles.imageContainer}>
              <h3>Original Image</h3>
              <img
                src={displayParams.url}
                alt="Original"
                onError={handleImageError("Original")}
                className={styles.image}
              />
              <div className={styles.urlDisplay}>
                <h4>Original URL:</h4>
                <code className={styles.url}>{displayParams.url}</code>
              </div>
            </div>

            <div className={styles.imageContainer}>
              <h3>Optimized Image</h3>
              <OptimizedImage
                src={displayParams.url}
                width={displayParams.width}
                height={displayParams.height}
                quality={displayParams.quality}
                format={displayParams.format as "webp" | "jpeg" | "png" | "avif" | undefined}
                fit={displayParams.fit as "cover" | "contain" | "fill" | "inside" | "outside"}
                alt="Optimized"
                onError={handleImageError("Optimized")}
                className={styles.image}
              />
              <div className={styles.urlDisplay}>
                <h4>Using OptimizedImage Component:</h4>
                <code className={styles.url}>
                  {`<OptimizedImage
  src="${displayParams.url}"${displayParams.width ? `\n  width={${displayParams.width}}` : ""}${
    displayParams.height ? `\n  height={${displayParams.height}}` : ""
  }
  quality={${displayParams.quality}}${displayParams.format ? `\n  format="${displayParams.format}"` : ""}
  fit="${displayParams.fit}"
  alt="Optimized"
/>`}
                </code>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.allowedBuckets}>
        <h3>Allowed S3 Buckets:</h3>
        <div className={styles.bucketList}>
          <code>
            aekveym7hu-product-prod, product-content-prod, product-stage, product-content-stage, product-qa-higharc,
            product-content-qa, product-develop, product-content-develop, product-content-localdev, product-sandbox1-8,
            product-content-sandbox1-8, higharc-product-test, higharc-image-catalog, photoreal-test
          </code>
        </div>
      </div>

      <Head>
        <title>{getPageTitle("Image Service Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
    </div>
  );
}

export const Route = createFileRoute("/debug/image-service")({
  component: ImageServicePage,
});
